import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";
import { http, ACCESS_KEY, TYPE_META, videoEmbedUrl } from "../components/resources/resourceUtils";
import { CardAction } from "../components/resources/ResourceLibraryCard";
import { ResourceThumb } from "../components/resources/ResourceThumb";
import { VideoLightbox } from "../components/resources/VideoLightbox";
import { AccessGate } from "../components/resources/AccessGate";
import "../styles/resources.css";

const InlineVideo = ({ r }) => {
    const embed = videoEmbedUrl(r.url);
    if (!embed) return null;
    return (
        <div className="aspect-video w-full overflow-hidden rounded-[18px] bg-[#1F1F14]" data-testid="resource-detail-video">
            <iframe title={r.title} src={embed.replace("autoplay=1", "autoplay=0")} className="w-full h-full"
                allow="fullscreen; picture-in-picture; encrypted-media" allowFullScreen />
        </div>
    );
};

const Detail = ({ r, categoryName }) => {
    const [watching, setWatching] = useState(null);
    const meta = TYPE_META[r.type] || TYPE_META.link;
    const embedded = r.type === "video" && videoEmbedUrl(r.url);
    return (
        <article className="grid gap-10 lg:gap-16 lg:grid-cols-[1.1fr_1fr] items-start" data-testid="resource-detail">
            {embedded ? <InlineVideo r={r} /> : (
                <div className="aspect-[4/3] overflow-hidden rounded-[18px] border" style={{ borderColor: "#E8E4DA" }}>
                    <ResourceThumb r={r} />
                </div>
            )}
            <div>
                <div className="flex flex-wrap items-center gap-3">
                    <span className="res-eyebrow" data-testid="resource-detail-category">{categoryName || "Resource"}</span>
                    <span className="inline-flex items-center gap-1.5 text-[13px] px-2.5 py-1 rounded-full" style={{ background: meta.tint }}>
                        <meta.Icon size={14} strokeWidth={1.6} aria-hidden /> {meta.label}
                    </span>
                </div>
                <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-[56px] leading-[1.06]" style={{ color: "#4A4A30" }} data-testid="resource-detail-title">{r.title}</h1>
                <div className="res-gold-rule mt-7" />
                {r.description && <p className="mt-7 text-[18px] leading-[1.8]" style={{ color: "#5C5C47" }} data-testid="resource-detail-description">{r.description}</p>}
                {!embedded && <div className="mt-10"><CardAction r={r} onWatch={setWatching} /></div>}
            </div>
            <VideoLightbox resource={watching} onClose={() => setWatching(null)} />
        </article>
    );
};

export default function ResourceDetailPage() {
    const { slug } = useParams();
    const [state, setState] = useState({ loading: true });
    const load = useCallback(async () => {
        setState({ loading: true });
        const { data: s } = await http.get("/client-resources/settings").catch(() => ({ data: {} }));
        if (!s.access_code_enabled) localStorage.removeItem(ACCESS_KEY);
        const token = localStorage.getItem(ACCESS_KEY);
        try {
            const { data } = await http.get(`/client-resources/item/${slug}`, { headers: token ? { "X-Resources-Access": token } : {} });
            setState({ loading: false, ...data });
        } catch (e) {
            const code = e?.response?.status;
            if (code === 403) localStorage.removeItem(ACCESS_KEY);
            setState({ loading: false, locked: code === 403, missing: code !== 403 });
        }
    }, [slug]);
    useEffect(() => { load(); }, [load]);

    return (
        <main className="ap-res min-h-screen">
            <Seo title={`${state.resource?.title || "Resource"} | Alchemy Practice`} description={state.resource?.description || "A resource from Alchemy Practice."} noindex />
            <Nav />
            <section className="pt-36 pb-24 lg:pt-44 lg:pb-32">
                <div className="mx-auto max-w-[1200px] px-6 lg:px-12">
                    <Link to="/resources" className="res-link inline-flex items-center gap-2" data-testid="resource-detail-back">
                        <ArrowLeft size={16} strokeWidth={1.6} /> All resources
                    </Link>
                    <div className="mt-10">
                        {state.loading ? <p data-testid="resource-detail-loading">Loading...</p>
                            : state.locked ? <AccessGate onUnlocked={load} />
                            : state.missing ? (
                                <div className="max-w-xl" data-testid="resource-detail-missing">
                                    <h1 className="font-serif text-4xl" style={{ color: "#4A4A30" }}>This resource is not available</h1>
                                    <p className="mt-5 text-[17px] leading-[1.8]" style={{ color: "#5C5C47" }}>
                                        It may have been moved or is no longer shared. You can browse everything that is available in the library.
                                    </p>
                                </div>
                            ) : <Detail r={state.resource} categoryName={state.category_name} />}
                    </div>
                    <p className="mt-24 pt-8 border-t text-[15px] leading-[1.7] max-w-2xl" style={{ borderColor: "#E8E4DA", color: "#5C5C47" }} data-testid="resource-detail-crisis-note">
                        These resources support your therapy and are not a substitute for care. If you are in crisis, call or text 988.
                    </p>
                </div>
            </section>
            <Footer />
        </main>
    );
}
